import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Clock, GitBranch, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge, Button, Card, CardContent, CardHeader } from '@/components/ui';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/Dialog';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { inferenceApi, type InferenceCandidate } from '@/lib/api';
import { cn } from '@/lib/cn';

type CandidateFilter = 'open' | 'deferred' | 'all';

export function InferenceQueue() {
  const { t, i18n } = useTranslation(['studio', 'common']);
  const qc = useQueryClient();
  const [filter, setFilter] = useState<CandidateFilter>('open');
  const [promoteTarget, setPromoteTarget] = useState<InferenceCandidate | null>(null);
  const [deferTarget, setDeferTarget] = useState<InferenceCandidate | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['studio', 'inference', 'candidates', filter],
    queryFn: () => inferenceApi.listCandidates(filter),
  });

  const review = useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: Parameters<typeof inferenceApi.reviewCandidate>[1];
    }) => inferenceApi.reviewCandidate(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['studio', 'inference', 'candidates'] });
      qc.invalidateQueries({ queryKey: ['studio', 'inference', 'rules'] });
      setPromoteTarget(null);
      setDeferTarget(null);
    },
  });

  const filterTabs: CandidateFilter[] = ['open', 'deferred', 'all'];

  return (
    <div className="space-y-4">
      <header className="flex items-end justify-between gap-3">
        <div>
          <h2 className="text-h2 text-brand-900">{t('studio:inference.queue.title')}</h2>
          <p className="mt-1 text-small text-slate-600">{t('studio:inference.queue.subtitle')}</p>
        </div>
        <div className="flex gap-1 rounded border border-slate-200 p-1">
          {filterTabs.map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setFilter(f)}
              className={cn(
                'rounded px-2 py-1 text-small transition-colors',
                filter === f
                  ? 'bg-brand-50 font-medium text-brand-900'
                  : 'text-slate-600 hover:bg-slate-50',
              )}
            >
              {t(`studio:inference.queue.filter.${f}`)}
            </button>
          ))}
        </div>
      </header>

      {isLoading && (
        <Card>
          <CardContent>
            <p className="text-small text-slate-500">{t('common:status.loading')}</p>
          </CardContent>
        </Card>
      )}

      {!isLoading && data && data.length === 0 && (
        <Card>
          <CardContent>
            <p className="text-body text-slate-500">{t('studio:inference.queue.noOpen')}</p>
          </CardContent>
        </Card>
      )}

      {data && data.length > 0 && (
        <>
          <p className="text-small text-slate-500">
            {t('studio:inference.queue.openCount', { count: data.length })}
          </p>
          <div className="space-y-3">
            {data.map((c) => (
              <CandidateCard
                key={c.candidate_id}
                candidate={c}
                onAccept={() =>
                  review.mutate({ id: c.candidate_id, payload: { action: 'accept_instances' } })
                }
                onReject={() =>
                  review.mutate({ id: c.candidate_id, payload: { action: 'reject' } })
                }
                onPromote={() => setPromoteTarget(c)}
                onDefer={() => setDeferTarget(c)}
                locale={i18n.language}
              />
            ))}
          </div>
        </>
      )}

      <PromoteDialog
        candidate={promoteTarget}
        onClose={() => setPromoteTarget(null)}
        onSubmit={(name, scope) =>
          promoteTarget &&
          review.mutate({
            id: promoteTarget.candidate_id,
            payload: { action: 'promote_to_rule', rule_name: name, rule_scope: scope },
          })
        }
        pending={review.isPending}
      />

      <DeferDialog
        candidate={deferTarget}
        onClose={() => setDeferTarget(null)}
        onSubmit={(until, reason) =>
          deferTarget &&
          review.mutate({
            id: deferTarget.candidate_id,
            payload: { action: 'defer', defer_until: until, defer_reason: reason },
          })
        }
        pending={review.isPending}
      />
    </div>
  );
}

interface CandidateCardProps {
  candidate: InferenceCandidate;
  locale: string;
  onAccept: () => void;
  onReject: () => void;
  onPromote: () => void;
  onDefer: () => void;
}

function CandidateCard({
  candidate,
  locale,
  onAccept,
  onReject,
  onPromote,
  onDefer,
}: CandidateCardProps) {
  const { t } = useTranslation('studio');
  const conf = candidate.avg_confidence;
  const interactable = candidate.status === 'open' || candidate.status === 'deferred';

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <p className="font-mono text-small text-slate-500">{candidate.pattern_signature}</p>
            <p className="mt-1 text-body text-slate-900">{candidate.pattern_description}</p>
          </div>
          <div className="flex flex-col items-end gap-1 text-small text-slate-500">
            <span>
              {t('inference.queue.columns.affected')}:{' '}
              <span className="font-semibold text-slate-900">{candidate.affected_entities}</span>
            </span>
            {conf != null && (
              <Badge
                variant={conf >= 0.85 ? 'success' : conf >= 0.6 ? 'warning' : 'neutral'}
              >
                {(conf * 100).toFixed(0)}%
              </Badge>
            )}
            <span>{new Date(candidate.surfaced_at).toLocaleDateString(locale)}</span>
            {candidate.status !== 'open' && (
              <Badge variant="neutral">{candidate.status}</Badge>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {candidate.example_triples.length > 0 && (
          <details>
            <summary className="cursor-pointer text-small text-accent-700 hover:text-accent">
              {t('inference.queue.columns.examples')} ({candidate.example_triples.length})
            </summary>
            <ul className="mt-2 space-y-1 font-mono text-small text-slate-700">
              {candidate.example_triples.slice(0, 5).map((tri, i) => (
                <li key={i} className="rounded bg-slate-50 px-2 py-1">
                  <span className="text-brand-900">{tri.subject}</span>{' '}
                  <span className="text-accent-700">{tri.predicate}</span>{' '}
                  <span>{tri.object}</span>
                </li>
              ))}
            </ul>
          </details>
        )}

        {interactable && (
          <div className="flex flex-wrap gap-2 pt-1">
            <Button size="sm" variant="secondary" onClick={onAccept}>
              <Check className="mr-1 h-3.5 w-3.5" aria-hidden />
              {t('inference.queue.actions.accept')}
            </Button>
            <Button size="sm" onClick={onPromote}>
              <GitBranch className="mr-1 h-3.5 w-3.5" aria-hidden />
              {t('inference.queue.actions.promote')}
            </Button>
            <Button size="sm" variant="ghost" onClick={onDefer}>
              <Clock className="mr-1 h-3.5 w-3.5" aria-hidden />
              {t('inference.queue.actions.defer')}
            </Button>
            <Button size="sm" variant="ghost" onClick={onReject}>
              <X className="mr-1 h-3.5 w-3.5" aria-hidden />
              {t('inference.queue.actions.reject')}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

interface PromoteDialogProps {
  candidate: InferenceCandidate | null;
  pending: boolean;
  onSubmit: (name: string, scope: string | undefined) => void;
  onClose: () => void;
}

function PromoteDialog({ candidate, pending, onSubmit, onClose }: PromoteDialogProps) {
  const { t } = useTranslation(['studio', 'common']);
  const [name, setName] = useState('');
  const [scope, setScope] = useState('');

  return (
    <Dialog open={!!candidate} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('studio:inference.queue.promote.title')}</DialogTitle>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (name.trim()) onSubmit(name.trim(), scope.trim() || undefined);
          }}
          className="space-y-3"
        >
          <label className="block text-small">
            <span className="text-slate-700">{t('studio:inference.queue.promote.name')}</span>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              minLength={2}
              autoFocus
            />
          </label>
          <label className="block text-small">
            <span className="text-slate-700">{t('studio:inference.queue.promote.scope')}</span>
            <Input
              value={scope}
              onChange={(e) => setScope(e.target.value)}
              placeholder={t('studio:inference.queue.promote.scopeHint')}
            />
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={onClose} disabled={pending}>
              {t('studio:inference.queue.promote.cancel')}
            </Button>
            <Button type="submit" disabled={pending || !name.trim()}>
              {t('studio:inference.queue.promote.submit')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

interface DeferDialogProps {
  candidate: InferenceCandidate | null;
  pending: boolean;
  onSubmit: (until: string, reason: string | undefined) => void;
  onClose: () => void;
}

function DeferDialog({ candidate, pending, onSubmit, onClose }: DeferDialogProps) {
  const { t } = useTranslation('studio');
  // default: re-surface in 7 days
  const defaultDate = new Date(Date.now() + 7 * 24 * 3600 * 1000).toISOString().slice(0, 10);
  const [until, setUntil] = useState(defaultDate);
  const [reason, setReason] = useState('');

  return (
    <Dialog open={!!candidate} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('inference.queue.defer.title')}</DialogTitle>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit(new Date(until).toISOString(), reason.trim() || undefined);
          }}
          className="space-y-3"
        >
          <label className="block text-small">
            <span className="text-slate-700">{t('inference.queue.defer.until')}</span>
            <Input
              type="date"
              value={until}
              onChange={(e) => setUntil(e.target.value)}
              required
              autoFocus
            />
          </label>
          <label className="block text-small">
            <span className="text-slate-700">{t('inference.queue.defer.reason')}</span>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
            />
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="submit" disabled={pending}>
              {t('inference.queue.defer.submit')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
